import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  checkInAttendance,
  confirmAttendanceCheckIn,
  fetchLogoutSummary,
  confirmAttendanceLogout,
  fetchBreakStatus,
  startShortBreak,
  endShortBreak,
  reloginAttendance,
  applyLeave,
  requestLeave,
  fetchLeaveStatus,
  cancelLeave,
  fetchDayActivityTimeline,
} from '../../services/attendanceService';
import { addNotification } from '../../store/slices/uiSlice';
import { setUserStatus } from '../../store/slices/authSlice';
import LogToWorkModal from './LogToWorkModal';
import LogoutNowModal from './LogoutNowModal';
import ShareAttendanceModal from './ShareAttendanceModal';
import ShortBreakModal from './ShortBreakModal';
import ApplyLeaveModal from './ApplyLeaveModal';
import HalfDayLeaveWarningModal from './HalfDayLeaveWarningModal';
import WelcomeBackModal from './WelcomeBackModal';
import DayActivityTimelineModal from './DayActivityTimelineModal';
import {
  notifyAttendanceSessionChanged,
  subscribeAttendanceSessionChanged,
} from '../../services/attendanceSessionBus';

const warningKey = (leaveEventId) => `attendance.leave.warning.${leaveEventId}`;
const logoutKey = (leaveEventId) => `attendance.leave.autologout.${leaveEventId}`;

const hasFired = (key) => {
  try {
    return Boolean(key && localStorage.getItem(key));
  } catch {
    return false;
  }
};

const markFired = (key) => {
  try {
    if (key) localStorage.setItem(key, '1');
  } catch {
    // ignore
  }
};

const LoginWorkLocationModal = ({ onSelect, onClose }) => (
  <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50">
    <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-2xl">
      <h3 className="text-lg font-bold text-gray-900 dark:text-white">Where are you working today?</h3>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
        Choose a work location. Your status stays Available.
      </p>
      <div className="mt-4 grid grid-cols-1 gap-2">
        <button
          type="button"
          onClick={() => onSelect('WORK_FROM_HOME')}
          className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-left text-sm font-semibold text-emerald-800 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200"
        >
          Work from home
        </button>
        <button
          type="button"
          onClick={() => onSelect('WORK_FROM_OFFICE')}
          className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-left text-sm font-semibold text-sky-800 hover:bg-sky-100 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-200"
        >
          Work from office
        </button>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="mt-3 w-full rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
      >
        Cancel
      </button>
    </div>
  </div>
);

const formatBreakTimer = (totalSeconds) => {
  const secs = Math.max(0, Number(totalSeconds) || 0);
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) {
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const BASE_ACTIONS = [
  {
    id: 'log-work',
    label: 'Logging to Work',
    description: 'Start your day with today’s briefing',
    ready: true,
    accent: 'from-emerald-500 to-teal-600',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    id: 'share-attendance',
    label: 'Share Attendance & Status',
    description: 'Let teammates view your attendance in Team Attendance',
    ready: true,
    accent: 'from-indigo-500 to-violet-600',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
      </svg>
    ),
  },
  {
    id: 'apply-leave',
    label: 'Apply Leave',
    description: 'Apply today or future leave — track status in the popup',
    ready: true,
    accent: 'from-amber-400 to-orange-500',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    id: 'logout',
    label: 'Logout Now',
    description: 'Wrap up your day and log out',
    ready: true,
    accent: 'from-rose-500 to-red-600',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
      </svg>
    ),
  },
  {
    id: 'short-break',
    label: 'Short Break',
    description: 'Pause work and track your break time',
    ready: true,
    accent: 'from-sky-500 to-cyan-600',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    id: 'day-timeline',
    label: 'Day Timeline',
    description: 'Login → breaks → logout on one line',
    ready: true,
    accent: 'from-slate-500 to-slate-700',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h10M4 18h7" />
      </svg>
    ),
  },
];

const AttendanceActions = ({ user, channelId, onAttendanceMessagePosted, onOpenTeamAttendance }) => {
  const dispatch = useDispatch();
  const [briefing, setBriefing] = useState(null);
  const [welcomeBack, setWelcomeBack] = useState(null);
  const [logoutSummary, setLogoutSummary] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showBreakModal, setShowBreakModal] = useState(false);
  const [showApplyLeaveModal, setShowApplyLeaveModal] = useState(false);
  const [showHalfDayWarning, setShowHalfDayWarning] = useState(false);
  const [leaveStatus, setLeaveStatus] = useState(null);
  const [leaveCountdown, setLeaveCountdown] = useState(null);
  const [breakStatus, setBreakStatus] = useState(null);
  const [breakElapsed, setBreakElapsed] = useState(0);
  const [loadingAction, setLoadingAction] = useState(null);
  const [error, setError] = useState('');
  const [dayTimeline, setDayTimeline] = useState(null);
  const [workLocationPrompt, setWorkLocationPrompt] = useState(null);
  const [pendingWorkLocation, setPendingWorkLocation] = useState(null);
  const autoLogoutInFlight = useRef(false);

  const postTimelineMessage = (message) => {
    if (message && channelId) {
      onAttendanceMessagePosted?.(message);
    } else {
      onAttendanceMessagePosted?.();
    }
  };

  const applyBreakStatus = (status) => {
    if (!status) return null;
    setBreakStatus(status);
    setBreakElapsed(status.elapsedSeconds || 0);
    return status;
  };

  const refreshBreakStatus = async ({ force = false } = {}) => {
    try {
      const status = await fetchBreakStatus({ force });
      return applyBreakStatus(status);
    } catch {
      return null;
    }
  };

  const refreshLeaveStatus = async () => {
    try {
      const status = await fetchLeaveStatus();
      setLeaveStatus(status);
      setLeaveCountdown(status?.secondsUntilStart ?? null);
      return status;
    } catch {
      return null;
    }
  };

  /** Refresh Login / Logout / Break buttons from server (and optional status payload). */
  const refreshActionBar = async (statusFromResponse = null) => {
    if (statusFromResponse) {
      applyBreakStatus(statusFromResponse);
      await refreshLeaveStatus();
      return statusFromResponse;
    }
    const [breakStat] = await Promise.all([
      refreshBreakStatus(),
      refreshLeaveStatus(),
    ]);
    return breakStat;
  };

  useEffect(() => {
    refreshActionBar();
  }, []);

  // Stay in sync when Relogin (or other session changes) happen from timeline / elsewhere
  useEffect(() => {
    return subscribeAttendanceSessionChanged((detail) => {
      if (detail?.status) {
        applyBreakStatus(detail.status);
      }
      refreshActionBar(detail?.status || null);
    });
  }, []);

  // While leave awaits Share Attendance approvers, refresh so approve/reject updates here.
  useEffect(() => {
    if (!leaveStatus?.pendingApproval) return undefined;
    const id = setInterval(() => {
      refreshLeaveStatus();
    }, 15000);
    return () => clearInterval(id);
  }, [leaveStatus?.pendingApproval, leaveStatus?.leaveEventId]);

  useEffect(() => {
    if (!breakStatus?.onBreak) return undefined;
    const startedMs = breakStatus.breakStartedAt
      ? Date.parse(breakStatus.breakStartedAt)
      : Date.now() - ((breakStatus.elapsedSeconds || 0) * 1000);
    const tick = () => {
      const base = Number.isFinite(startedMs) ? startedMs : Date.now();
      setBreakElapsed(Math.max(0, Math.floor((Date.now() - base) / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [breakStatus?.onBreak, breakStatus?.breakStartedAt, breakStatus?.elapsedSeconds]);

  const triggerAutoLogout = async (status) => {
    const leaveEventId = status?.leaveEventId;
    if (!leaveEventId || hasFired(logoutKey(leaveEventId)) || autoLogoutInFlight.current) {
      return;
    }
    if (status?.loggedOut) {
      markFired(logoutKey(leaveEventId));
      return;
    }
    autoLogoutInFlight.current = true;
    try {
      const summary = await fetchLogoutSummary();
      setLogoutSummary({
        ...summary,
        comment: summary.comment || `Auto logout for half day leave${status?.reason ? `: ${status.reason}` : ''}`,
      });
      const result = await confirmAttendanceLogout(
        summary.comment || `Auto logout for half day leave${status?.reason ? `: ${status.reason}` : ''}`,
      );
      postTimelineMessage(result?.message);
      markFired(logoutKey(leaveEventId));
      await refreshActionBar();
      notifyAttendanceSessionChanged();
      dispatch(addNotification({
        type: 'success',
        message: 'Half day leave started — logged out automatically',
      }));
      setLogoutSummary(null);
    } catch (err) {
      try {
        const summary = await fetchLogoutSummary();
        setLogoutSummary(summary);
      } catch {
        // ignore
      }
      dispatch(addNotification({
        type: 'error',
        message: err?.response?.data?.error || err?.message || 'Failed to auto logout for half day leave',
      }));
    } finally {
      autoLogoutInFlight.current = false;
    }
  };

  useEffect(() => {
    if (!leaveStatus?.hasLeave || leaveStatus.leaveType !== 'HALF_DAY') {
      setShowHalfDayWarning(false);
      return undefined;
    }

    const evaluate = async () => {
      const status = await refreshLeaveStatus();
      if (!status?.hasLeave || status.leaveType !== 'HALF_DAY') {
        setShowHalfDayWarning(false);
        return;
      }
      const leaveEventId = status.leaveEventId;
      setLeaveCountdown(status.secondsUntilStart ?? null);

      if (status.warningWindow && !hasFired(warningKey(leaveEventId))) {
        setShowHalfDayWarning(true);
      }
      if (status.autoLogoutDue) {
        if (!hasFired(warningKey(leaveEventId))) {
          setShowHalfDayWarning(true);
        }
        await triggerAutoLogout(status);
      }
    };

    evaluate();
    const id = setInterval(evaluate, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaveStatus?.leaveEventId, leaveStatus?.leaveType, leaveStatus?.hasLeave]);

  useEffect(() => {
    if (!leaveStatus?.hasLeave || leaveStatus.leaveType !== 'HALF_DAY') {
      setLeaveCountdown(null);
      return undefined;
    }
    if (leaveStatus.secondsUntilStart == null) return undefined;
    if (leaveStatus.secondsUntilStart <= 0) {
      setLeaveCountdown(0);
      return undefined;
    }
    const targetMs = Date.now() + (leaveStatus.secondsUntilStart * 1000);
    const tick = () => {
      setLeaveCountdown(Math.max(0, Math.ceil((targetMs - Date.now()) / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [
    leaveStatus?.leaveEventId,
    leaveStatus?.hasLeave,
    leaveStatus?.leaveType,
    leaveStatus?.secondsUntilStart,
  ]);

  const isLoggedIn = Boolean(breakStatus?.checkedIn && !breakStatus?.loggedOut);

  const actions = useMemo(() => {
    return BASE_ACTIONS
      .filter((action) => {
        if (action.id === 'log-work') return !isLoggedIn;
        if (action.id === 'logout' || action.id === 'short-break') return isLoggedIn;
        return true;
      })
      .map((action) => {
        if (action.id === 'short-break' && breakStatus?.onBreak) {
          return {
            ...action,
            label: `On Break · ${formatBreakTimer(breakElapsed)}`,
            description: breakStatus.reason
              ? `${breakStatus.reason} — tap to join back`
              : 'Tap to join back',
            accent: 'from-amber-500 to-orange-600',
          };
        }
        if (action.id === 'log-work') {
          return {
            ...action,
            label: 'Login',
            description: 'Start your day with today’s briefing',
          };
        }
        return action;
      });
  }, [breakStatus, breakElapsed, isLoggedIn]);

  const handleCancelLeave = async (leaveId) => {
    const result = await cancelLeave(leaveId);
    postTimelineMessage(result?.message);
    setLeaveStatus(result?.status || null);
    setShowHalfDayWarning(false);
    const session = await refreshActionBar();
    const onBreak = Boolean(session?.onBreak);
    dispatch(setUserStatus({
      status: session?.loggedOut
        ? 'LOGGED_OUT'
        : (onBreak ? 'AWAY' : (session?.checkedIn ? 'AVAILABLE' : 'OFFLINE')),
      statusMessage: onBreak ? (session?.reason || null) : null,
      statusSource: 'ATTENDANCE',
    }));
    notifyAttendanceSessionChanged({ status: session || null });
    dispatch(addNotification({
      type: 'success',
      message: result?.leaveTypeLabel
        ? `${result.leaveTypeLabel} leave reverted`
        : 'Leave reverted',
    }));
    return result;
  };

  const handleAction = async (action) => {
    if (!action.ready) return;

    if (action.id === 'share-attendance') {
      setShowShareModal(true);
      return;
    }

    if (action.id === 'day-timeline') {
      setLoadingAction(action.id);
      setError('');
      try {
        const data = await fetchDayActivityTimeline();
        setDayTimeline(data);
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || 'Failed to open day timeline');
      } finally {
        setLoadingAction(null);
      }
      return;
    }

    if (action.id === 'apply-leave') {
      setShowApplyLeaveModal(true);
      return;
    }

    if (action.id === 'short-break') {
      setLoadingAction(action.id);
      setError('');
      try {
        const status = await refreshBreakStatus();
        if (status && !status.checkedIn) {
          setError('Log to work first before taking a short break.');
          return;
        }
        if (status?.loggedOut) {
          setError('You have already logged out for today.');
          return;
        }
        setShowBreakModal(true);
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || 'Failed to open short break');
      } finally {
        setLoadingAction(null);
      }
      return;
    }

    setLoadingAction(action.id);
    setError('');
    try {
      if (action.id === 'log-work') {
        setWorkLocationPrompt('log-work');
        return;
      } else if (action.id === 'logout') {
        const data = await fetchLogoutSummary();
        setLogoutSummary(data);
        await refreshActionBar();
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || `Failed to open ${action.label}`);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleRelogin = async (workLocation) => {
    const location = workLocation || pendingWorkLocation;
    if (!location) {
      setWorkLocationPrompt('relogin');
      return;
    }
    try {
      const result = await reloginAttendance(location);
      dispatch(setUserStatus({ status: 'AVAILABLE', workLocation: location }));
      postTimelineMessage(result?.message);
      setLogoutSummary(null);
      setWelcomeBack(null);
      await refreshActionBar(result?.status || null);
      notifyAttendanceSessionChanged({ status: result?.status || null });
      dispatch(addNotification({
        type: 'success',
        message: result?.durationLabel
          ? `Relogged in · ${result.durationLabel} counted as break`
          : 'Relogged in — you are back to active work',
      }));
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to relogin';
      dispatch(addNotification({ type: 'error', message: msg }));
      await refreshActionBar();
      throw err;
    }
  };

  const startLoginAfterLocation = async (workLocation) => {
    setPendingWorkLocation(workLocation);
    setLoadingAction('log-work');
    setError('');
    try {
      const data = await checkInAttendance();
      if (data?.requiresRelogin) {
        setWelcomeBack({
          userName: data.userDisplayName
            || user?.firstName
            || user?.username
            || 'there',
          gapLabel: data.reloginGapLabel,
          logoutTimeLabel: data.logoutTimeLabel,
        });
        return;
      }
      setBriefing(data);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to open Login');
    } finally {
      setLoadingAction(null);
    }
  };

  const handleWorkLocationChosen = async (workLocation) => {
    const prompt = workLocationPrompt;
    setWorkLocationPrompt(null);
    if (prompt === 'relogin') {
      await handleRelogin(workLocation);
      return;
    }
    await startLoginAfterLocation(workLocation);
  };

  return (
    <>
      <div className="border-t border-emerald-200/80 dark:border-emerald-900/50 bg-gradient-to-b from-emerald-50/90 to-white dark:from-emerald-950/40 dark:to-gray-900 px-4 py-4">
        <div className="max-w-3xl mx-auto">
          <div className="mb-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
              Attendance
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
              Your check-in and logout history — only you can see your progress here.
            </p>
          </div>

          {error && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/40 dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {leaveStatus?.hasLeave && (
            <div className="mb-3 rounded-xl border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-950/40 px-3.5 py-2.5 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wider text-violet-700 dark:text-violet-300">
                  {leaveStatus.pendingApproval ? 'Leave pending' : 'On leave'}
                </p>
                <p className="text-sm text-violet-900 dark:text-violet-100 truncate mt-0.5">
                  {leaveStatus.leaveTypeLabel || leaveStatus.leaveType || 'Leave'}
                  {leaveStatus.reason ? ` · ${leaveStatus.reason}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleCancelLeave(leaveStatus.leaveEventId)}
                className="flex-shrink-0 text-xs font-bold px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-violet-300 dark:border-violet-700 text-violet-700 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900/40"
              >
                Revert leave
              </button>
            </div>
          )}

          {leaveStatus?.hasLeave && !leaveStatus.pendingApproval && leaveStatus.leaveType === 'HALF_DAY' && (
            <div className="mb-3 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 px-3.5 py-2.5">
              <p className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-300">
                Half day leave approved
              </p>
              <p className="text-sm text-amber-900 dark:text-amber-100 truncate mt-0.5">
                {leaveStatus.startTimeLabel || leaveStatus.startTime || 'Starts soon'}
                {' – '}
                {leaveStatus.endTimeLabel || leaveStatus.endTime || ''}
                {leaveCountdown != null && leaveCountdown > 0
                  ? ` · starts in ${Math.floor(leaveCountdown / 60)}m ${leaveCountdown % 60}s`
                  : leaveStatus.halfDayStarted
                    ? ' · in progress'
                    : ''}
              </p>
            </div>
          )}

          {leaveStatus?.hasLeave && leaveStatus.pendingApproval && (
            <div className="mb-3 rounded-xl border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-950/40 px-3.5 py-2.5">
              <p className="text-xs font-semibold uppercase tracking-wider text-sky-700 dark:text-sky-300">
                Leave pending approval
              </p>
              <p className="text-sm text-sky-900 dark:text-sky-100 truncate mt-0.5">
                Open Apply Leave to view status
                {leaveStatus.approverNames?.length
                  ? ` · waiting on ${leaveStatus.approverNames.join(', ')}`
                  : ''}
              </p>
            </div>
          )}

          {breakStatus?.onBreak && (
            <div className="mb-3 rounded-xl border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-950/40 px-3.5 py-2.5 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wider text-sky-700 dark:text-sky-300">
                  Short break in progress
                </p>
                <p className="text-sm text-sky-900 dark:text-sky-100 truncate mt-0.5">
                  {breakStatus.reason || 'On break'} · started {breakStatus.breakStartedAtLabel || 'earlier'}
                </p>
              </div>
              <span className="flex-shrink-0 text-lg font-bold tabular-nums text-sky-800 dark:text-sky-100">
                {formatBreakTimer(breakElapsed)}
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {actions.map((action) => (
              <button
                key={action.id}
                type="button"
                disabled={!action.ready || Boolean(loadingAction)}
                onClick={() => handleAction(action)}
                className={`group relative overflow-hidden rounded-xl border text-left transition ${
                  action.ready
                    ? 'border-emerald-200 dark:border-emerald-800 bg-white dark:bg-gray-800 hover:shadow-md hover:border-emerald-400 dark:hover:border-emerald-600 cursor-pointer'
                    : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 opacity-70 cursor-not-allowed'
                } ${action.id === 'short-break' && breakStatus?.onBreak
                  ? 'border-amber-300 dark:border-amber-700 ring-1 ring-amber-200/70 dark:ring-amber-800/50'
                  : ''}`}
              >
                <div className="flex items-start gap-3 p-3.5">
                  <span className={`flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br ${action.accent} text-white flex items-center justify-center shadow-sm`}>
                    {action.icon}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-gray-900 dark:text-white">
                      {loadingAction === action.id
                        ? (action.id === 'log-work'
                          ? 'Opening briefing…'
                          : action.id === 'short-break'
                            ? 'Opening break…'
                            : 'Loading summary…')
                        : action.label}
                    </span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {action.description}
                    </span>
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {workLocationPrompt && (
        <LoginWorkLocationModal
          onSelect={handleWorkLocationChosen}
          onClose={() => setWorkLocationPrompt(null)}
        />
      )}

      {welcomeBack && (
        <WelcomeBackModal
          userName={welcomeBack.userName}
          gapLabel={welcomeBack.gapLabel}
          logoutTimeLabel={welcomeBack.logoutTimeLabel}
          onClose={() => setWelcomeBack(null)}
          onRelogin={handleRelogin}
        />
      )}

      {briefing && (
        <LogToWorkModal
          briefing={briefing}
          userName={user?.firstName || user?.username || 'there'}
          onConfirm={async () => {
            try {
              const result = await confirmAttendanceCheckIn(pendingWorkLocation);
              dispatch(setUserStatus({
                status: 'AVAILABLE',
                workLocation: pendingWorkLocation || null,
              }));
              postTimelineMessage(result?.message);
              await refreshActionBar(result?.status || null);
              notifyAttendanceSessionChanged({ status: result?.status || null });
              dispatch(addNotification({
                type: 'success',
                message: 'Logged in — posted to Attendance timeline',
              }));
            } catch (err) {
              const msg = err?.response?.data?.error || err?.message || 'Failed to confirm check-in';
              dispatch(addNotification({ type: 'error', message: msg }));
              throw err;
            }
          }}
          onClose={() => setBriefing(null)}
        />
      )}

      {logoutSummary && (
        <LogoutNowModal
          summary={logoutSummary}
          userName={user?.firstName || user?.username || 'there'}
          readOnly={logoutSummary.alreadyLoggedOut}
          onRelogin={logoutSummary.canRelogin ? handleRelogin : undefined}
          onConfirm={async (comment) => {
            try {
              const result = await confirmAttendanceLogout(comment);
              postTimelineMessage(result?.message);
              await refreshActionBar(result?.status || null);
              notifyAttendanceSessionChanged({ status: result?.status || null });
              dispatch(addNotification({
                type: 'success',
                message: 'Logged out — posted to Attendance timeline',
              }));
            } catch (err) {
              const msg = err?.response?.data?.error || err?.message || 'Failed to confirm logout';
              dispatch(addNotification({ type: 'error', message: msg }));
              throw err;
            }
          }}
          onClose={() => setLogoutSummary(null)}
        />
      )}

      {showHalfDayWarning && leaveStatus?.hasLeave && leaveStatus.leaveType === 'HALF_DAY' && (
        <HalfDayLeaveWarningModal
          leaveStatus={leaveStatus}
          secondsUntilStart={leaveCountdown}
          onClose={() => {
            if (leaveStatus?.leaveEventId) {
              markFired(warningKey(leaveStatus.leaveEventId));
            }
            setShowHalfDayWarning(false);
          }}
          onAcknowledge={() => {
            if (leaveStatus?.leaveEventId) {
              markFired(warningKey(leaveStatus.leaveEventId));
            }
          }}
          onCancelLeave={async () => handleCancelLeave(leaveStatus?.leaveEventId)}
        />
      )}

      {showBreakModal && (
        <ShortBreakModal
          status={breakStatus}
          onClose={() => setShowBreakModal(false)}
          onStartBreak={async (reason) => {
            const result = await startShortBreak(reason);
            postTimelineMessage(result?.message);
            await refreshActionBar(result?.status || null);
            notifyAttendanceSessionChanged({ status: result?.status || null });
            dispatch(addNotification({
              type: 'success',
              message: 'Short break started',
            }));
          }}
          onJoinBack={async () => {
            const result = await endShortBreak();
            postTimelineMessage(result?.message);
            await refreshActionBar(result?.status || null);
            notifyAttendanceSessionChanged({ status: result?.status || null });
            dispatch(addNotification({
              type: 'success',
              message: result?.durationLabel
                ? `Joined back · break lasted ${result.durationLabel}`
                : 'Joined back from short break',
            }));
          }}
        />
      )}

      {showApplyLeaveModal && (
        <ApplyLeaveModal
          onClose={() => {
            setShowApplyLeaveModal(false);
            refreshActionBar();
          }}
          onApplyToday={async ({ leaveType, reason, startTime, endTime }) => {
            const result = await applyLeave({ leaveType, reason, startTime, endTime });
            postTimelineMessage(result?.message);
            setLeaveStatus(result?.status || null);
            const leaveLabel = result?.leaveTypeLabel
              ? `${result.leaveTypeLabel} leave${reason ? ` · ${reason}` : ''}`
              : (reason || 'On leave');
            dispatch(setUserStatus({
              status: 'ON_LEAVE',
              statusMessage: leaveLabel,
              statusSource: 'ATTENDANCE',
            }));
            setLeaveCountdown(result?.status?.secondsUntilStart ?? null);
            await refreshActionBar();
            notifyAttendanceSessionChanged();
            const timing = result?.startTime && result?.endTime
              ? ` · ${result.startTime}–${result.endTime}`
              : '';
            const pending = result?.status?.pendingApproval;
            const approvers = result?.status?.approverNames?.length
              ? ` · sent to ${result.status.approverNames.join(', ')}`
              : '';
            dispatch(addNotification({
              type: 'success',
              message: result?.leaveTypeLabel
                ? `${result.leaveTypeLabel} leave ${pending ? 'submitted for approval' : 'applied'} for today${timing}${approvers}`
                : `Leave ${pending ? 'submitted for approval' : 'applied'} for today`,
            }));
            if (!pending && result?.status?.autoLogoutDue) {
              await triggerAutoLogout(result.status);
            } else if (!pending && result?.status?.warningWindow) {
              setShowHalfDayWarning(true);
            }
            return result;
          }}
          onRequestFuture={async (fromDate, toDate, reason) => {
            const result = await requestLeave(fromDate, toDate, reason);
            postTimelineMessage(result?.message);
            await refreshActionBar();
            notifyAttendanceSessionChanged();
            const approvers = result?.approverNames?.length
              ? ` · sent to ${result.approverNames.join(', ')}`
              : '';
            dispatch(addNotification({
              type: 'success',
              message: result?.dayCount
                ? `Leave request sent for approval · ${result.dayCount} day${result.dayCount === 1 ? '' : 's'}${approvers}`
                : `Leave request sent for approval${approvers}`,
            }));
            return result;
          }}
          onCancelLeave={async (leaveId) => handleCancelLeave(leaveId)}
        />
      )}

      <ShareAttendanceModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        onShared={() => {
          dispatch(addNotification({
            type: 'success',
            message: 'Attendance shared — they can view it in Team Attendance',
          }));
          refreshActionBar();
          onOpenTeamAttendance?.();
        }}
      />

      {dayTimeline && (
        <DayActivityTimelineModal
          timeline={dayTimeline}
          onClose={() => setDayTimeline(null)}
          onChangeDate={async (nextDate) => {
            const data = await fetchDayActivityTimeline(nextDate);
            setDayTimeline(data);
          }}
        />
      )}
    </>
  );
};

export default AttendanceActions;
